import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCanvasStore } from '@/stores/canvasStore';
import { useProfileStore } from '@/stores/profileStore';
import { InfiniteCanvas } from '@/components/canvas/InfiniteCanvas';
import { TopBar } from '@/components/toolbar/TopBar';
import { BottomToolbar } from '@/components/toolbar/BottomToolbar';
import { FocusFloater } from '@/extensions/focus-timer/FocusFloater';
import { WallView } from '@/components/wall/WallView';
import { MobileLibraryContainer } from '@/components/library/MobileLibraryContainer';
import { BoardsView } from '@/components/boards/BoardsView';
import { ZoomControls, ZoomNotification } from '@/components/canvas/ZoomControls';
import { FloatingHomeButton } from '@/components/canvas/FloatingHomeButton';
import { Breadcrumbs } from '@/components/navigation/Breadcrumbs';
import { MindDumpFlow } from '@/components/onboarding/MindDumpFlow';
import { useCanvasEventLogger } from '@/hooks/useCanvasEventLogger';
// BoardProfileWidget removed - profile now accessible via right-click context menu
import { TooltipProvider } from '@/components/ui/tooltip';
import { LaunchLoader } from '@/components/brand/LaunchLoader';
import { useIsMobile } from '@/hooks/use-mobile';
import { AnimatePresence, motion } from 'framer-motion';

const Canvas = () => {
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const {
    fetchBoards,
    fetchItems,
    fetchAllItems,
    setCurrentBoard,
    navigateToBoard,
    boards,
    currentBoardId,
    isDarkMode,
    isLoading,
    viewMode,
    setViewMode,
    wallScrollOffset,
  } = useCanvasStore();

  const {
    hasCompletedOnboarding,
    isLoading: isProfileLoading,
    loadProfile,
  } = useProfileStore();

  // Load user profile on mount
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Log canvas events (board open/close, item creation, session tracking)
  useCanvasEventLogger();

  // Initialize dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Handle URL view mode
  useEffect(() => {
    const viewFromUrl = searchParams.get('view');
    if (viewFromUrl === 'discover' || viewFromUrl === 'wall') {
      setViewMode('discover');
    }
    // 'focus' is the default (no ?view param), so no need to check
  }, [searchParams, setViewMode]);

  // Force discover mode on mobile
  useEffect(() => {
    if (isMobile && viewMode !== 'discover') {
      setViewMode('discover');
    }
  }, [isMobile, viewMode, setViewMode]);

  // Fetch boards and all items on mount (allItems needed for item counts in sidebar/board cards)
  useEffect(() => {
    fetchBoards().then(() => {
      // One-time repair: create board_link items for any child boards
      // that are missing them (fixes onboarding race condition)
      useCanvasStore.getState().repairMissingBoardLinks();
    });
    fetchAllItems();
  }, [fetchBoards, fetchAllItems]);

  // Migrate stranded items to safe positions when board loads
  useEffect(() => {
    if (currentBoardId && !isLoading) {
      const { rescueStrandedItems } = useCanvasStore.getState();
      const count = rescueStrandedItems();
      if (count > 0) {
        console.log(`[Canvas] Migrated ${count} items to safe positions`);
      }
    }
  }, [currentBoardId, isLoading]);

  // Track previous view mode to detect switches
  const prevViewModeRef = React.useRef<string | null>(null);

  // Handle initial board navigation
  // - If ?board= URL param exists, navigate to that board (deep link)
  // - If in Create/Focus mode with no board, navigate to home board
  // - If switching TO focus mode, always fit-to-view
  useEffect(() => {
    if (boards.length === 0) return;

    const boardIdFromUrl = searchParams.get('board');
    const switchedToFocus = prevViewModeRef.current !== null && 
                            prevViewModeRef.current !== 'focus' && 
                            viewMode === 'focus';
    
    // If there's a board in URL and no current board, navigate to it
    if (!currentBoardId && boardIdFromUrl) {
      const targetBoard = boards.find(b => b.id === boardIdFromUrl);
      if (targetBoard) {
        setCurrentBoard(targetBoard.id);
        fetchItems(targetBoard.id);
        useCanvasStore.setState({ shouldFitToView: true });
        if (viewMode !== 'focus') {
          setViewMode('focus');
        }
      }
    }
    // If in focus mode with no board, auto-navigate to home board
    else if (!currentBoardId && viewMode === 'focus') {
      const homeBoard = boards.find(b => b.parent_id === null);
      if (homeBoard) {
        setCurrentBoard(homeBoard.id);
        fetchItems(homeBoard.id);
        useCanvasStore.setState({ shouldFitToView: true });
      }
    }
    // If switched to focus mode with a board already selected, fit-to-view
    else if (switchedToFocus && currentBoardId) {
      useCanvasStore.setState({ shouldFitToView: true });
    }
    
    // Update previous view mode ref
    prevViewModeRef.current = viewMode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boards, viewMode]);

  // Prevent browser zoom on Ctrl+wheel (canvas handles its own zoom)
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const renderView = () => {
    if (isLoading && boards.length === 0) {
      return <LaunchLoader label="Loading your workspace" fill="parent" />;
    }

    switch (viewMode) {
      case 'discover':
        return <WallView />;
      case 'focus':
        // Create mode: always show canvas (auto-navigates to home if no board)
        return <InfiniteCanvas />;
      default:
        return <InfiniteCanvas />;
    }
  };

  // Profile loading gate: show spinner while profile is being fetched
  // Without this, the canvas renders blank because neither the onboarding
  // flow nor the main canvas is ready yet.
  if (isProfileLoading) {
    return <LaunchLoader label="Loading your workspace" />;
  }

  // Phones get the Library (Everything, Spaces, search) instead of the
  // canvas chrome. See docs/mobile-library/plan.md.
  if (isMobile) {
    return (
      <TooltipProvider>
        <MobileLibraryContainer />
        <FocusFloater />
      </TooltipProvider>
    );
  }

  // Onboarding gate: DISABLED — users go straight to canvas.
  // To re-enable, uncomment the block below.
  // if (!hasCompletedOnboarding) { ... }

  return (
    <TooltipProvider>
      <div className="h-screen w-screen overflow-hidden bg-canvas fixed inset-0">
        <TopBar scrollOffset={wallScrollOffset} />
        
        {/* Floating Home & Board buttons - only when viewing a board in Focus mode */}
        {viewMode === 'focus' && currentBoardId && <FloatingHomeButton />}
        
        {/* Breadcrumbs - only in focus mode when inside a nested board */}
        {viewMode === 'focus' && currentBoardId && (
          <div className="fixed top-24 left-0 right-0 z-40 flex justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <Breadcrumbs />
            </div>
          </div>
        )}
        
        <main className="h-full w-full">
          {renderView()}
        </main>
        
        <ZoomNotification />
        <ZoomControls />
        <BottomToolbar />
        <FocusFloater />

      </div>
    </TooltipProvider>
  );
};

export default Canvas;
